import { useChat } from "@ai-sdk/react";
import { eventIteratorToStream, ORPCError } from "@orpc/client";
import {
	assistantTools,
	AssistantUITools,
	type AssistantToolExecutors,
	type AssistantToolName,
	type AssistantUIMessage,
} from "@repo/api/client";
import { JobType } from "@repo/db/client";
import type { SubmitReferralPriorAuthParams } from "@repo/services/browserAgentJobs";
import { normalizePayor, supportsPayor } from "@repo/config/payors";
import {
	eligibilityPayorsForTenant,
	getDemoFeatures,
	payorLabelsForStaff,
	priorAuthPayorsForTenant,
} from "@repo/config/tenants";
import { formatTimestampRelative } from "@repo/utils/dateUtils";
import { useRefCurrent } from "@repo/utils/hooks/useRefCurrent";
import { useStableCallback } from "@repo/utils/hooks/useStableCallback";
import {
	isToolUIPart,
	lastAssistantMessageIsCompleteWithToolCalls,
	type ChatStatus,
	type UIMessage,
	type UIMessagePart,
} from "ai";
import { useCallback, useMemo, useState } from "react";
import { ContextChip } from "../components/ContextChip";
import { useApiClient } from "../contexts/ApiContext";
import { useLoggedInUser } from "../contexts/AuthContext";
import { useDatabase } from "../db/DatabaseProvider";
import { Task, TaskId } from "../db/schema";
import { getTenantSlug } from "../tenant";

function useToolHandlers(taskId: TaskId): AssistantToolExecutors {
	const db = useDatabase();
	const getTask = useStableCallback((): Task | undefined => {
		return db.run("task", (q) => q.where("id", "=", taskId))[0];
	});

	const user = useLoggedInUser();
	const api = useApiClient();

	const toolExecutors = useMemo(
		(): AssistantToolExecutors => ({
			call_provider: async ({ phoneNumber, query, callReceiver }) => {
				console.log("Phone call initiated", {
					type: "information_gathering",
					phoneNumber,
					taskId,
				});

				if (!query) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "Questions are required to call a provider",
					};
				}

				const result = await api.tools.makePhoneCall({
					phoneNumber,
					query,
					taskId,
					callReceiver,
				});

				return result;
			},
			search_healthcare_providers: async (params) => {
				// Add preprocessing similar to agent toolExecutors
				const currentTask = getTask();
				let searchParams: typeof params & {
					insuranceId?: string;
					allowedOrganizationIds?: string[];
				} = params;

				if (currentTask) {
					// Add default location preferences if not provided and no identifying
					// info was given (NPI, provider name, or org name are specific enough
					// that location defaults would incorrectly filter results)
					const hasIdentifyingInfo =
						params.providerNpi ||
						params.providerName ||
						params.organizationName;
					if (
						!hasIdentifyingInfo &&
						(!params.locations || params.locations.length === 0)
					) {
						const department = db.run("department", (q) =>
							q.where("name", "=", currentTask.department),
						)[0];

						if (department?.defaultLocationPreferences) {
							searchParams = {
								...searchParams,
								locations: department.defaultLocationPreferences,
							};
						}
					}

					// Add patient's primary insurance for sorting if available
					if (currentTask.patient) {
						const patient = db.run("patient", (q) =>
							q.where("id", "=", currentTask.patient),
						)[0];

						if (patient) {
							const primaryInsurance = patient.insurance.find(
								(i) => i.isPrimary,
							);
							if (primaryInsurance?.id) {
								searchParams = {
									...searchParams,
									insuranceId: primaryInsurance.id,
								};
							}
						}
					}

					// Add department allowed organization IDs if configured
					const department = db.run("department", (q) =>
						q.where("name", "=", currentTask.department),
					)[0];

					if (department?.allowedOrganizationIds) {
						searchParams = {
							...searchParams,
							allowedOrganizationIds: department.allowedOrganizationIds,
						};
					}
				}

				return await api.tools.searchProviders(searchParams);
			},
			submit_referral_prior_authorization: async (params) => {
				// Set task to pending status before starting browser agent

				// Generate dates
				const today = new Date();
				const nextYearStart = new Date(today.getFullYear() + 1, 0, 1); // January 1st of next year

				const formatDate = (date: Date) => {
					const month = (date.getMonth() + 1).toString().padStart(2, "0");
					const day = date.getDate().toString().padStart(2, "0");
					const year = date.getFullYear();
					return `${month}/${day}/${year}`;
				};

				const currentTask = getTask();
				// Use diagnosisCodes from params if provided, otherwise fallback to task parameters
				const diagnosisCodes =
					(params.diagnosisCodes?.length ? params.diagnosisCodes : null) ??
					currentTask?.linkedTaskParameters?.diagnosisCodes ??
					[];

				if (!currentTask?.patient) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Expected to find patient to submit prior auth for but none found",
					};
				}

				if (!diagnosisCodes || diagnosisCodes.length === 0) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Diagnosis codes are required to submit prior authorization but none were found. Please report this to the team.",
					};
				}
				// Get patient's primary provider information
				const patient = db.run("patient", (q) =>
					q.where("id", "=", currentTask.patient),
				)[0]!;

				const primaryInsurance = patient.insurance.find((i) => i.isPrimary);
				if (!primaryInsurance) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "Expected to find primary insurance but none found",
					};
				}

				// Split patient name: if comma exists, use existing logic; otherwise split on spaces and take first/last
				let patientFirstName: string;
				let patientLastName: string;
				if (patient.name.includes(",")) {
					// Use existing comma-based logic
					const [lastName, firstName] = patient.name.split(", ");
					if (!lastName || !firstName) {
						return {
							success: false,
							code: "INPUT_VALIDATION_ERROR",
							message: `Expected comma-separated name format "Last, First", but got: ${patient.name}. Please report this to the team.`,
						};
					}
					patientFirstName = firstName;
					patientLastName = lastName;
				} else {
					// Split on spaces and process name parts
					const nameParts = patient.name.trim().split(/\s+/);
					if (nameParts.length < 2) {
						return {
							success: false,
							code: "INPUT_VALIDATION_ERROR",
							message: `Expected patient name to have at least first and last name, but got: ${patient.name}. Please report this to the team.`,
						};
					}

					// If there are more than 2 parts, remove single-character elements (this is getting rid of middle initials)
					let processedParts = nameParts;
					if (nameParts.length > 2) {
						processedParts = nameParts.filter((part) => part.length > 1);
					}

					// First element is first name, everything else is last name
					patientFirstName = processedParts[0]!;
					patientLastName = processedParts.slice(1).join(" ");
				}

				if (!patientLastName || !patientFirstName) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Failed to split patient name. Please report this to the team.",
					};
				}

				if (!patient.dateOfBirth || !primaryInsurance.payor) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Expected to find patient birthday and payor but none found. Please report this to the team.",
					};
				}

				// Same fold as the API-side gate in toolExecutors.ts: rows written
				// before payor canonicalization still hold spellings like
				// "United HealthCare " that mean a supported payor.
				const payor = normalizePayor(primaryInsurance.payor);

				// Skipped when the tenant's prior auths are faked, matching the
				// API-side gate: that job never reaches a portal.
				const tenantSlug = getTenantSlug();
				const supportedPayors = tenantSlug
					? priorAuthPayorsForTenant(tenantSlug)
					: [];
				const filesRealPriorAuth =
					!tenantSlug ||
					getDemoFeatures(tenantSlug)?.sendRealPriorAuth !== false;
				if (filesRealPriorAuth && !supportsPayor(supportedPayors, payor)) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: `Prior authorization is not supported for "${primaryInsurance.payor}". Supported plans are ${payorLabelsForStaff(supportedPayors).join(", ")}. If that is wrong, correct the payor on the patient's insurance record.`,
					};
				}

				const patientInfo: SubmitReferralPriorAuthParams["patientInfo"] = {
					memberId: primaryInsurance.memberId,
					firstName: patientFirstName,
					lastName: patientLastName,
					dateOfBirth: new Date(patient.dateOfBirth).toLocaleDateString(
						"en-US",
						{
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
						},
					),
					payor,
				};

				// Use primaryCareProvider from params if provided, otherwise fallback to patient record
				let pcpNpi: string;
				if (params.primaryCareProviderNpi) {
					// When primaryCareProviderNpi is provided via params, use it directly
					pcpNpi = params.primaryCareProviderNpi;
				} else {
					if (!patient.primaryProvider) {
						return {
							success: false,
							code: "INPUT_VALIDATION_ERROR",
							message:
								"Patient does not have a primary care provider assigned. Provide the PCP's name/NPI or run an eligibility check to pull their PCP.",
						};
					}

					if (!patient.primaryProvider.npi) {
						return {
							success: false,
							code: "INPUT_VALIDATION_ERROR",
							message:
								"Patient's primary care provider does not have an NPI on file. Please provide the PCP's NPI or run an eligibility check to update their information.",
						};
					}

					pcpNpi = patient.primaryProvider.npi;
				}

				// Add hardcoded and manually retrieved fields
				const fullParams: SubmitReferralPriorAuthParams = {
					department: currentTask.department,
					patientInfo: patientInfo,
					pcpNpi: pcpNpi,
					authDetails: {
						diagnosisCodes: diagnosisCodes,
						fromDate: formatDate(today),
						toDate: formatDate(nextYearStart),
						quantity: undefined,
						procedureCode: "99214", // 99244 no longer valid code
					},
					renderingProvider: params.provider,
					taskId: taskId,
					userId: user?.id,
				};

				const result = await api.tools.executeBrowserAgentJob({
					browserAgentJobTask: JobType.ReferralPriorAuth,
					parameters: fullParams,
				});

				// // Add activity log entry
				// const activityContent = `Referral prior authorization request submitted via browser agent\nPatient: ${params.patient.firstName} ${params.patient.lastName}\nJob ID: ${result.jobId}`;
				// addActivityLogItem(activityContent);

				return {
					success: true,
					jobId: result.jobId,
					status: result.status,
				};
			},
			send_message_to_patient: async ({
				message,
			}): Promise<AssistantUITools["send_message_to_patient"]["output"]> => {
				const currentTask = getTask();
				if (!currentTask?.patient) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Error while sending message to patient: expected current task to have a patient but none found",
					};
				}
				const result = await api.tools.sendMessageToPatient({
					patientId: currentTask.patient,
					message,
					department: currentTask.department,
					taskIds: [currentTask.id],
				});
				// Handle UserActionRequiredResponse returned by the handler
				if ("code" in result && result.code === "USER_ACTION_REQUIRED") {
					return result;
				}
				// At this point, result has the status field (not UserActionRequiredResponse)
				const statusResult = result as Exclude<
					typeof result,
					{ code: "USER_ACTION_REQUIRED" }
				>;
				if (statusResult.status === "declined") {
					return {
						success: false,
						code: "CONSENT_DECLINED",
						consentStatus: "declined",
						message:
							"Failed to send message because the patient declined to receive text messages containing PHI",
					};
				} else if (statusResult.status === "consent-request-in-flight") {
					return {
						success: false,
						code: "CONSENT_REQUEST_IN_FLIGHT",
						lastRequestedAt: statusResult.lastRequestedAt.toISOString(),
						consentStatus: "consent-request-in-flight",
						message: `You did not send the original message. A consent request was already sent to the patient ${formatTimestampRelative(statusResult.lastRequestedAt)}. The user should wait for the patient's response before sending additional text messages about referrals.`,
					};
				} else if (statusResult.status === "consent-requested") {
					return {
						success: true,
						consentStatus: "requested",
						message:
							"You did not send the original message. Instead you sent a consent request to the patient to discuss referrals over text. The user should wait for the patient's response before sending additional text messages about referrals.",
					};
				} else if (statusResult.status === "outside-business-hours") {
					return {
						success: false,
						code: "OUTSIDE_BUSINESS_HOURS",
						message: statusResult.message,
					};
				} else {
					// statusResult.status === "sent"
					return {
						success: true,
						consentStatus: "consented",
						message: "Message sent successfully to patient",
					};
				}
			},
			attach_records_send_referral: async (params) => {
				// Set task to pending status before starting browser agent

				const currentTask = getTask();
				if (!currentTask) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "No task found",
					};
				}

				if (!currentTask.department) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "No department found",
					};
				}

				if (params.priorAuthorizationDetails.priorAuthRequired === "Unknown") {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Unknown if patient requires prior authorization or not. Run an eligibility check for the patient first to determine if prior authorization is required.",
					};
				}

				const diagnosisCodes =
					currentTask.linkedTaskParameters?.diagnosisCodes || [];

				// Transform priorAuthorizationDetails to match browser agent API expectations
				const transformedPriorAuthDetails =
					params.priorAuthorizationDetails?.priorAuthRequired === true
						? {
								authorizationNumber:
									params.priorAuthorizationDetails.authorizationNumber,
								numberOfVisitsAllowed:
									params.priorAuthorizationDetails.numberOfVisitsAllowed,
								validFromDate: params.priorAuthorizationDetails.validFromDate,
								validToDate: params.priorAuthorizationDetails.validToDate,
								providerName: params.priorAuthorizationDetails.providerName,
								providerNpi: params.priorAuthorizationDetails.providerNpi,
							}
						: undefined;

				const result = await api.tools.executeBrowserAgentJob({
					browserAgentJobTask: JobType.AttachRecordsSendReferral,
					parameters: {
						diagnosisCodes: diagnosisCodes,
						context: params.relevantNotes,
						referralRecipient: {
							name: params.provider.name,
							addressLine1: params.provider.addressLine1,
							city: params.provider.city,
							state: params.provider.state,
							zipCode: params.provider.zipCode,
							faxNumber: params.provider.faxNumber,
							providerId: params.provider.providerId,
							organizationId: params.provider.organizationId,
						},
						priorAuthorizationDetails: transformedPriorAuthDetails,
						department: currentTask?.department,
						taskId: taskId as string,
						userId: user?.id,
					},
				});

				// // Add activity log entry
				// const activityContent = `Medical records attached and referral sent via fax for ${params.referral.patientName}\nJob ID: ${result.jobId}`;
				// addActivityLogItem(activityContent);

				return {
					success: true,
					jobId: result.jobId,
					status: result.status,
				};
			},
			check_patient_eligibility: async () => {
				// Set task to pending status before starting browser agent

				const currentTask = getTask();
				const patient = db.run("patient", (q) =>
					q.where("id", "=", currentTask?.patient),
				)[0];
				if (!patient) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "Patient not found",
					};
				}

				// Use primary insurance for eligibility check
				const primaryInsurance = patient.insurance.find((ins) => ins.isPrimary);
				if (!primaryInsurance) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "No primary insurance found for patient",
					};
				}

				if (!primaryInsurance.payor) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "Insurance payor name is missing for primary insurance",
					};
				}

				const payor = normalizePayor(primaryInsurance.payor);

				// Eligibility, not prior auth. This gate used to use the prior-auth
				// list, so it refused checks the API-side path would have run.
				//
				// Skipped when the tenant's eligibility checks are faked, matching
				// the API-side gate: that job fabricates its result and never
				// reaches a portal, so a route is not a precondition for it.
				const eligibilityTenantSlug = getTenantSlug();
				const eligiblePayors = eligibilityTenantSlug
					? eligibilityPayorsForTenant(eligibilityTenantSlug)
					: [];
				const checksRealEligibility =
					!eligibilityTenantSlug ||
					getDemoFeatures(eligibilityTenantSlug)?.sendRealEligibilityCheck !==
						false;
				if (checksRealEligibility && !supportsPayor(eligiblePayors, payor)) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: `Eligibility checks are not supported for "${primaryInsurance.payor}". Supported plans are ${payorLabelsForStaff(eligiblePayors).join(", ")}. If that is wrong, correct the payor on the patient's insurance record.`,
					};
				}

				if (!patient.dateOfBirth) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message: "Patient date of birth is missing",
					};
				}

				if (!currentTask?.department) {
					return {
						success: false,
						code: "INPUT_VALIDATION_ERROR",
						message:
							"Department is required for eligibility check to determine the requesting provider",
					};
				}

				const result = await api.tools.executeBrowserAgentJob({
					browserAgentJobTask: JobType.CheckEligibility,
					parameters: {
						patientInsuranceId: primaryInsurance.memberId,
						patientInsuranceRecordId: primaryInsurance.patientInsuranceId,
						dateOfBirth: new Date(patient.dateOfBirth).toLocaleDateString(
							"en-US",
							{
								year: "numeric",
								month: "2-digit",
								day: "2-digit",
							},
						),
						payorName: payor,
						department: currentTask.department,
						taskId: taskId,
						userId: user?.id,
					},
				});

				return {
					success: true,
					jobId: result.jobId,
					status: result.status,
				};
			},
			// update_provider_info: async (params) => {
			// 	// Helper function to clean phone numbers by removing special characters
			// 	const cleanPhoneNumber = (phoneNumber: string): string => {
			// 		return phoneNumber.replace(/[^\d]/g, "");
			// 	};

			// 	// Map the tool parameters to the updateHealthcareEntities input format
			// 	const updateInput = {
			// 		provider: params.provider
			// 			? {
			// 					npi: params.provider.npi,
			// 					name: params.provider.name,
			// 					phoneNumber: cleanPhoneNumber(params.provider.phoneNumber),
			// 					specialties: params.provider.specialty
			// 						? params.provider.specialty
			// 								.split(",")
			// 								.map((s) => s.trim())
			// 								.filter((s) => s.length > 0)
			// 						: [],
			// 					faxNumber: params.provider.faxNumber
			// 						? cleanPhoneNumber(params.provider.faxNumber)
			// 						: undefined,
			// 					emailAddress: params.provider.emailAddress,
			// 					offersTelehealth: params.provider.offersTelehealth,
			// 					age: params.provider.age,
			// 					gradYear: params.provider.gradYear,
			// 					gender: params.provider.gender,
			// 					specialtyDetails: params.provider.specialtyDetails,
			// 				}
			// 			: undefined,
			// 		organization: params.organization
			// 			? {
			// 					companyName: params.organization.companyName,
			// 					npi: params.organization.npi,
			// 					type: params.organization.type,
			// 				}
			// 			: undefined,
			// 		locations: (params.locations || []).map((location) => ({
			// 			...location,
			// 			phoneNumber: cleanPhoneNumber(location.phoneNumber),
			// 			faxNumber: location.faxNumber
			// 				? cleanPhoneNumber(location.faxNumber)
			// 				: undefined,
			// 		})),
			// 	};

			// 	console.log("Updating healthcare entities with input:", updateInput);

			// 	// Call the updateHealthcareEntities API endpoint
			// 	const result = await api.tools.updateHealthcareEntities(updateInput);

			// 	if (result.success) {
			// 		// Return simplified data without complex objects/dates
			// 		return {
			// 			success: true,
			// 			message: "Healthcare entities created/updated successfully",
			// 			result: {
			// 				updatedLocations:
			// 					result.locations?.map((location) => ({
			// 						id: String(location.id),
			// 						address: String(location.address),
			// 						city: String(location.city),
			// 						state: String(location.state),
			// 						phoneNumber: String(location.phoneNumber),
			// 						faxNumber: location.faxNumber
			// 							? String(location.faxNumber)
			// 							: undefined,
			// 						latitude: location.latitude
			// 							? String(location.latitude)
			// 							: undefined,
			// 						longitude: location.longitude
			// 							? String(location.longitude)
			// 							: undefined,
			// 					})) || [],
			// 				updatedOrganization: result.organization
			// 					? {
			// 							id: String(result.organization.id),
			// 							name: String(result.organization.companyName),
			// 							npi: result.organization.npi
			// 								? String(result.organization.npi)
			// 								: undefined,
			// 							type: result.organization.type
			// 								? String(result.organization.type)
			// 								: undefined,
			// 						}
			// 					: undefined,
			// 				updatedProvider: result.provider
			// 					? {
			// 							id: String(result.provider.id),
			// 							name: String(result.provider.providerName),
			// 							npi: String(result.provider.npi),
			// 							phoneNumber: params.provider?.phoneNumber || "",
			// 							specialty: params.provider?.specialty || "Unknown",
			// 							faxNumber: params.provider?.faxNumber,
			// 							emailAddress: result.provider.emailAddress
			// 								? String(result.provider.emailAddress)
			// 								: undefined,
			// 							offersTelehealth:
			// 								result.provider.offersTelehealth ?? undefined,
			// 							age: result.provider.age ?? undefined,
			// 							gradYear: result.provider.gradYear ?? undefined,
			// 							gender:
			// 								(result.provider.gender as "male" | "female" | "other") ??
			// 								undefined,
			// 							specialtyDetails: result.provider.specialtyDetails
			// 								? String(result.provider.specialtyDetails)
			// 								: undefined,
			// 						}
			// 					: undefined,
			// 			},
			// 		};
			// 	} else {
			// 		throw new Error(
			// 			result.message || "Failed to update healthcare entities",
			// 		);
			// 	}
			// },
		}),
		[api.tools, db, getTask, taskId, user?.id],
	);

	return toolExecutors;
}

type ToolCallArgs = {
	[ToolName in AssistantToolName]: {
		toolName: ToolName;
		toolCallId: string;
		input: AssistantUITools[ToolName]["input"];
	};
}[AssistantToolName];

export type ConfirmToolCallArg<
	ToolName extends AssistantToolName = AssistantToolName,
> =
	| { permission: "approve"; input?: AssistantUITools[ToolName]["input"] }
	| {
			permission: "reject";
			output?: AssistantUITools[ToolName]["output"];
	  };

export type ConfirmToolCallFn = <
	ToolName extends AssistantToolName = AssistantToolName,
>(
	toolCallId: string,
	arg: ConfirmToolCallArg<ToolName>,
) => void;

export function useAssistantChat({
	taskId,
	chips,
}: {
	taskId: TaskId;
	chips: ContextChip[];
}): {
	messages: AssistantUIMessage[];
	sendMessage: (message: string) => Promise<void>;
	status: ChatStatus;
	error: Error | undefined;
	setMessages: (messages: AssistantUIMessage[]) => void;
	clearError: () => void;
	confirmToolCall: ConfirmToolCallFn;
	pendingToolCalls: ToolCallArgs[];
	startNewChat: () => Promise<void>;
} {
	const api = useApiClient();
	const toolExecutors = useToolHandlers(taskId);

	const [chatId, setChatId] = useState<string>(() => crypto.randomUUID());

	const chipsRef = useRefCurrent(chips);

	const [confirmationPendingToolCalls, setConfirmationPendingToolCalls] =
		useState<Set<ToolCallArgs>>(new Set());

	const pendingToolCallsArray = useMemo(
		() => Array.from(confirmationPendingToolCalls),
		[confirmationPendingToolCalls],
	);

	// A tiny wrapper around lastAssistantMessageIsCompleteWithToolCalls to only
	// consider tool calls that are pending confirmation
	const lastAssistantMessageIsCompleteWithConfirmedToolCalls =
		useStableCallback(({ messages }: { messages: UIMessage[] }) => {
			return (
				lastAssistantMessageIsCompleteWithToolCalls({ messages }) &&
				pendingToolCallsArray.length === 0
			);
		});

	const {
		addToolResult,
		messages,
		sendMessage: sendMessageInternal,
		status,
		error,
		setMessages,
		clearError,
		stop,
	} = useChat<AssistantUIMessage>({
		id: chatId,
		sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithConfirmedToolCalls,
		transport: {
			async sendMessages(options) {
				const chips = chipsRef.current;
				return eventIteratorToStream(
					await api.assistant.chat(
						{
							messages: options.messages,
							patientId: chips.find((chip) => chip.type === "patient")?.id,
							taskId: chips.find((chip) => chip.type === "task")?.id,
						},
						{ signal: options.abortSignal },
					),
				);
			},
			reconnectToStream() {
				throw new Error("Unsupported");
			},
		},
		onToolCall({ toolCall }) {
			const toolName = toolCall.toolName as AssistantToolName;
			const tool = assistantTools[toolName];

			if (tool.confirmationRequired) {
				setConfirmationPendingToolCalls(
					(prev) =>
						new Set([
							...prev,
							{
								toolName,
								toolCallId: toolCall.toolCallId,
								input: toolCall.input,
							} as ToolCallArgs,
						]),
				);
				return;
			}

			executeTool({
				toolCallId: toolCall.toolCallId,
				toolName: toolName,
				input: toolCall.input,
			});
		},
	});

	const executeTool = useCallback(
		({
			toolCallId,
			toolName,
			input,
		}: {
			toolCallId: string;
			toolName: AssistantToolName;
			input: any;
		}) => {
			const executor = toolExecutors[toolName];

			if (!executor) {
				throw new Error(`Unknown tool called ${toolName}`);
			}

			// DON'T AWAIT HERE (can lead to deadlock)
			void executor(input as any)
				.then((output) =>
					addToolResult({
						output,
						tool: toolName,
						toolCallId: toolCallId,
					}),
				)
				.catch((error: unknown) => {
					// All caught errors are system errors
					// User-actionable outcomes are now returned as UserActionRequiredResult, not thrown
					const message =
						error instanceof ORPCError ? error.message : String(error);

					void addToolResult({
						output: {
							success: false,
							code: "INTERNAL_ERROR",
							message,
						},
						tool: toolName,
						toolCallId: toolCallId,
					});
				});
		},
		[addToolResult, toolExecutors],
	);

	const sendMessage = useStableCallback(async (message: string) => {
		const confirmationPendingTools = Array.from(confirmationPendingToolCalls);

		setConfirmationPendingToolCalls(new Set());

		// Cancel all tool calls that are pending confirmation
		setMessages((messages) => {
			const newMessages = messages.map((message): AssistantUIMessage => {
				const newParts = message.parts.map(
					(part): UIMessagePart<never, AssistantUITools> => {
						if (!isToolUIPart(part)) return part;
						const confirmationPendingTool = confirmationPendingTools.find(
							(tool) => tool.toolCallId === part.toolCallId,
						);
						if (confirmationPendingTool) {
							return {
								...part,
								state: "output-available",
								output: {
									success: false,
									code: "REJECTED_BY_USER",
									message: "Tool call cancelled by user",
								},
							} as any;
						}
						return part;
					},
				);
				return { ...message, parts: newParts };
			});

			return newMessages;
		});

		await sendMessageInternal({ text: message });
	});

	const confirmToolCall = useCallback<ConfirmToolCallFn>(
		(toolCallId: string, arg) => {
			const toolCall = confirmationPendingToolCalls
				.values()
				.find((toolCall) => toolCall.toolCallId === toolCallId);
			if (!toolCall) {
				throw new Error(
					"Tried to confirm tool call that wasn't pending confirmation",
				);
			}

			const { toolName, input } = toolCall;

			setConfirmationPendingToolCalls(
				(prev) =>
					new Set(
						[...prev].filter((toolCall) => toolCall.toolCallId !== toolCallId),
					),
			);

			if (arg.permission === "approve") {
				// Update messages with new input
				setMessages((messages) => {
					const newMessages = messages.map((message): AssistantUIMessage => {
						return {
							...message,
							parts: message.parts.map((part) =>
								isToolUIPart(part) && part.toolCallId === toolCallId
									? ({ ...part, input: arg.input ?? input } as any)
									: part,
							),
						};
					});
					return newMessages;
				});
				executeTool({
					toolCallId,
					toolName,
					input: arg.input ?? input,
				});
			} else {
				addToolResult({
					output: arg.output ?? {
						success: false,
						code: "REJECTED_BY_USER",
						message: "Tool call rejected by user",
					},
					tool: toolName,
					toolCallId: toolCallId,
				}).catch((error) =>
					console.error(`Failed to add tool result ${toolName}`, error),
				);
			}
		},
		[addToolResult, confirmationPendingToolCalls, executeTool, setMessages],
	);

	const startNewChat = useStableCallback(async () => {
		setChatId(crypto.randomUUID());
		clearError();
		setConfirmationPendingToolCalls(new Set());
		await stop();
	});

	return {
		messages,
		sendMessage,
		status,
		error,
		setMessages,
		clearError,
		confirmToolCall,
		pendingToolCalls: pendingToolCallsArray,
		startNewChat,
	};
}
